import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useModal from '../../hooks/useModal';
import Modal from './Modal';
import GamePopup from './GamePopup';
import gamesService from '../../services/games';
import { resolveApiImageUrl } from '../../utils/logoUtils';
import './GameSearchPopup.css';

/**
 * 루미나 전역 게임 검색 팝업 — 새 화면 부품(블랙에 없음, SPEC §11.1 "전역 게임 검색을 헤더에 상시 노출").
 * 열림: 상단 바 검색 버튼(TopNavBar)이 window 에 보내는 `lumina:search` 이벤트. 닫힘: 닫기 버튼·바깥 클릭·ESC·화면 이동.
 * 데이터: services/games.js 의 searchGames(게임 검색 API·폴백)·searchProviders(게임사 목록 필터) — 입력 0.3초 디바운스, 최소 1자.
 * 실행: 게임 카드 클릭 = 블랙 슬롯 화면과 같은 흐름(로그인 필요 시 로그인으로, launchGame → GamePopup, 실패 사유는 공용 모달, 닫으면 확정 종료).
 * 게임사 결과: 칩으로 보여 주고, 누르면 그 게임사의 목록(슬롯 게임사는 슬롯 목록, 카지노 전용은 카지노 목록 — 슬롯 화면과 같은 목록 API)을 격자에 채운다.
 * 시안 팝업 구조(.gl-overlay .gl-popup .gl-head .gl-grid .gl-item .gl-prov .ph)를 그대로 쓴다. 이 부품은 TopNavBar 끝에서 <GameSearchPopup /> 로 마운트한다.
 */
const GameSearchPopup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // WHY: 실행 실패 사유를 네이티브 alert 대신 공용 정적 모달로 노출(슬롯 화면과 같은 규칙)
  const { isOpen: isAlertOpen, modalConfig: alertConfig, showAlert, closeModal: closeAlert } = useModal();

  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [games, setGames] = useState([]);
  const [providers, setProviders] = useState([]);
  /* [2026-09-30] 불러오지 못한 게임사 그림 주소 — 그 칩은 그림이 없는 칩처럼 이름 첫 글자를 그린다(종전: 그림만 숨겨 빈 원이 남음, 09-28 대조 ⑥) */
  const [failedProvImgs, setFailedProvImgs] = useState({});
  const [activeProvider, setActiveProvider] = useState(null);
  const [searching, setSearching] = useState(false);

  // 게임 실행 팝업 상태(슬롯 화면과 동일)
  const [showGamePopup, setShowGamePopup] = useState(false);
  const [gameUrl, setGameUrl] = useState('');
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameLoading, setGameLoading] = useState(false);

  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const seqRef = useRef(0);
  const showGamePopupRef = useRef(false);

  useEffect(() => { showGamePopupRef.current = showGamePopup; }, [showGamePopup]);

  const close = useCallback(() => {
    setOpen(false);
    setKeyword('');
    setGames([]);
    setProviders([]);
    setActiveProvider(null);
    setSearching(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  // 상단 바의 검색 버튼 → 열기
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('lumina:search', onOpen);
    return () => window.removeEventListener('lumina:search', onOpen);
  }, []);

  // 열려 있는 동안: 본문 스크롤 잠금, 입력 초점, ESC 닫기(게임 실행 덧창이 떠 있으면 그쪽 ESC 가 먼저다)
  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';
    if (inputRef.current) inputRef.current.focus();
    const onKey = (e) => {
      if (e.key === 'Escape' && !showGamePopupRef.current) close();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // 화면이 바뀌면 닫는다
  useEffect(() => {
    if (open) close();
    // [2026-11-01] 켜 둔 게임 창도 닫는다 — 슬롯·홈에서 켠 게임은 화면을 벗어나면 끝나는데 이 창은 상단 메뉴에 붙어 남아,
    //   게임 중 안내 창의 [쪽지 페이지로 이동]을 눌러도 게임 창이 쪽지 화면을 계속 가렸다(독립 검토). 종료 처리는 닫기 단추와 같다.
    if (showGamePopup) closeGamePopup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  // [2026-11-01] 안내 창 [쪽지 페이지로 이동] — 이미 쪽지 화면이면 주소가 안 바뀌어 위 효과가 돌지 않으므로 같은 알림으로 게임 창을 닫는다(독립 검토)
  useEffect(() => {
    const onGotoNotices = () => { if (showGamePopup) closeGamePopup(); };
    window.addEventListener('unread-notice-goto-list', onGotoNotices);
    return () => window.removeEventListener('unread-notice-goto-list', onGotoNotices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGamePopup]);

  // 검색어 입력 → 0.3초 디바운스 → 게임·게임사 동시 조회(늦게 온 옛 응답은 버린다)
  const handleKeywordChange = (e) => {
    const value = e.target.value;
    setKeyword(value);
    setActiveProvider(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    const kw = value.trim();
    if (!kw) {
      setGames([]);
      setProviders([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    timerRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      try {
        const [gameRes, providerRes] = await Promise.all([
          gamesService.searchGames(kw),
          gamesService.searchProviders(kw).catch(() => ({ providers: [] }))
        ]);
        if (seq !== seqRef.current) return;
        const gameList = (gameRes && (gameRes.results || gameRes.games || gameRes.data)) || [];
        setGames(gameList);
        setProviders((providerRes && providerRes.providers) || []);
      } catch (err) {
        if (seq !== seqRef.current) return;
        console.error('[GameSearchPopup] 검색 실패:', err);
        setGames([]);
        setProviders([]);
      } finally {
        if (seq === seqRef.current) setSearching(false);
      }
    }, 300);
  };

  // 게임사 칩 → 그 게임사의 게임 목록(슬롯 화면 handleProviderClick 과 같은 목록 API·코드 정규화)
  const handleProviderClick = async (provider) => {
    const code = (provider.provider || provider.provider_name || provider.name || '').toString();
    // [2026-11-01] 띄어쓰기를 밑줄로 바꾸지 않는다 — 게임사 표준 코드에 띄어쓰기가 있는 곳(NOLIMIT CITY·PG SOFT)은 밑줄 표기로 물으면 서버가 게임 0개를
    //   돌려줘 게임 목록이 비었다(2026-11-01 ur3 실측: 노리밋시티 139·PG소프트 177 → 0). 서버는 대소문자를 무시하고 표준 코드와 그대로 맞춘다.
    const vendor = code.toLowerCase();
    const types = Array.isArray(provider.game_types) ? provider.game_types : String(provider.game_types || '').split(',');
    const gameType = types.map((t) => String(t).trim().toLowerCase()).includes('slot') ? 'slot' : 'casino';
    const seq = ++seqRef.current;
    setActiveProvider(provider);
    setSearching(true);
    try {
      const data = await gamesService.getGameList({ vendor, game_type: gameType });
      if (seq !== seqRef.current) return;
      setGames((data && (data.games || data.data)) || []);
    } catch (err) {
      if (seq !== seqRef.current) return;
      console.error('[GameSearchPopup] 게임사 목록 로드 실패:', err);
      setGames([]);
    } finally {
      if (seq === seqRef.current) setSearching(false);
    }
  };

  // 게임 실행(슬롯 화면 handleGameClick 과 같은 흐름)
  const handleGameClick = async (game) => {
    if (!user) {
      close();
      navigate('/login');
      return;
    }

    try {
      setGameLoading(true);
      setSelectedGame(game);

      // 게임 실행 URL 가져오기
      const launchData = await gamesService.launchGame(game.id);

      if (launchData && launchData.success && launchData.gameUrl) {
        setGameUrl(launchData.gameUrl);
        setShowGamePopup(true);
      } else {
        showAlert(launchData?.error || '게임을 실행할 수 없습니다.');
      }
    } catch (error) {
      // services/games.js 가 axios 에러를 `throw error.response?.data || error` 로 던지므로 error 는 plain object({ error:'...' }) 또는 Error.
      console.error('게임 실행 실패:', error);
      // 안 읽은 쪽지 차단은 services/api.js 가 쪽지 안내 창을 연다 — 같은 내용의 알림창을 겹쳐 띄우지 않는다(2026-11-01 ur3 실측: 창 2개 겹침)
      if (error?.code === 'UNREAD_NOTICE_GATE') return;
      const reason = error?.error || error?.message || '게임을 실행할 수 없습니다.';
      showAlert(reason);
    } finally {
      setGameLoading(false);
    }
  };

  const closeGamePopup = () => {
    setShowGamePopup(false);
    setGameUrl('');
    setSelectedGame(null);
    // 회원이 덧창을 직접 닫았다 → 게임이 확실히 끝난 신호(확정 종료). 슬롯 화면과 동일
    gamesService.endGame(true).catch(err => {
      console.error('게임 종료 처리 실패:', err);
    });
  };

  /* 표시 도우미 — 유저 API 가 썸네일 없음에 문자열 'null' 을 내려보내는 경우(실측)는 없음으로 본다 */
  const hasThumb = (url) => !!url && url !== 'null';
  const nameOf = (game) => game.game_name_ko || game.game_name || '';
  const vendorOf = (game) => game.vendor_name_ko || game.vendor_name || game.category_name_ko || game.provider || game.vendor || game.category_name || '';
  const firstChar = (text) => ((text || '?').trim().charAt(0) || '?');
  const providerName = (p) => p.name_ko || p.name || p.provider_name || p.provider || '';
  const providerImage = (p) => {
    if (p.character_hidden !== true && p.character_url) return resolveApiImageUrl(p.character_url);
    if (p.logo_hidden !== true && p.logo_url) return resolveApiImageUrl(p.logo_url);
    return null;
  };
  const kw = keyword.trim();

  return (
    <>
      {open && (
        <div className="gl-overlay gs-overlay" onClick={close}>
          <div className="gl-popup" role="dialog" aria-modal="true" aria-label="게임 검색" onClick={(e) => e.stopPropagation()}>
            <div className="gl-head">
              <div className="gl-tt">
                <h3>게임 검색</h3>
                {kw && !searching && (
                  <span className="tag">
                    {games.length.toLocaleString('ko-KR')} 게임{!activeProvider && providers.length > 0 ? ` · ${providers.length.toLocaleString('ko-KR')} 게임사` : ''}
                  </span>
                )}
              </div>
              <label className="gl-search">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.2" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></svg>
                <input
                  ref={inputRef}
                  type="search"
                  placeholder="게임 이름 또는 게임사..."
                  value={keyword}
                  onChange={handleKeywordChange}
                  autoComplete="off"
                  aria-label="게임 이름 또는 게임사"
                />
              </label>
              <button className="gl-close" type="button" onClick={close} aria-label="닫기">
                <svg viewBox="0 0 24 24" fill="none" strokeWidth="2.4" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
              </button>
            </div>

            {/* 게임사 결과 — 칩. 누르면 그 게임사 목록으로 격자를 바꾼다 */}
            {kw && providers.length > 0 && (
              <div className="gs-provs" aria-label="게임사 검색 결과">
                {providers.map((p) => {
                  const img = providerImage(p);
                  const isOn = activeProvider && (activeProvider.provider || activeProvider.name) === (p.provider || p.name);
                  return (
                    <button
                      key={p.id || p.provider || p.name}
                      type="button"
                      className={`gs-prov${isOn ? ' on' : ''}`}
                      onClick={() => handleProviderClick(p)}
                    >
                      <span className="gs-prov-im">{img && !failedProvImgs[img] ? <img src={img} alt="" loading="lazy" onError={() => setFailedProvImgs((m) => ({ ...m, [img]: true }))} /> : <span className="gs-prov-ph">{firstChar(providerName(p))}</span>}</span>
                      <span className="gs-prov-nm">{providerName(p)}</span>
                      {p.game_count != null && p.game_count !== '' && !isNaN(Number(p.game_count)) && (
                        <span className="gs-prov-cnt">{Number(p.game_count).toLocaleString('ko-KR')}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="gl-grid">
              {searching && <div className="gl-empty">검색 중...</div>}
              {!searching && games.map((game) => (
                <div
                  key={game.id}
                  className="gl-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleGameClick(game)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGameClick(game); } }}
                >
                  <div className="gl-thumb">
                    <span className="ph" aria-hidden="true">{firstChar(nameOf(game))}</span>
                    {hasThumb(game.thumbnail_url) && (
                      <img src={game.thumbnail_url} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    )}
                  </div>
                  <div className="gl-name">{nameOf(game)}</div>
                  {vendorOf(game) && <div className="gl-prov">{vendorOf(game)}</div>}
                </div>
              ))}
              {!searching && kw && games.length === 0 && <div className="gl-empty">검색 결과가 없습니다</div>}
            </div>
          </div>
        </div>
      )}

      {/* 게임 실행 팝업 */}
      {showGamePopup && (
        <GamePopup
          game={selectedGame}
          gameUrl={gameUrl}
          onClose={closeGamePopup}
          loading={gameLoading}
        />
      )}

      {/* 실패 사유 안내용 공용 모달 — id 는 다른 화면의 모달과 겹치지 않게 고유값 */}
      <Modal id="gameSearchAlertModal" isOpen={isAlertOpen} onClose={closeAlert} text={alertConfig.text} type={alertConfig.type} />
    </>
  );
};

export default GameSearchPopup;
